import { matchRoutes } from 'react-router-dom';
import routes, { type AppRoute } from '../../config/routes';

type Access = Record<string, boolean | undefined>;
export function filterAuthorizedRoutes(
  items: AppRoute[],
  access: Access,
): AppRoute[] {
  return items.flatMap((route) => {
    if (route.hideInMenu || !route.name) return [];
    if (route.access && !access[route.access]) return [];
    if (route.permissionResourceCode && !access[route.permissionResourceCode])
      return [];
    if (route.routes) {
      const children = filterAuthorizedRoutes(route.routes, access);
      return children.length ? [{ ...route, routes: children }] : [];
    }
    return route.component ? [route] : [];
  });
}
export function firstAuthorizedPath(
  items: AppRoute[],
  access: Access,
): string | undefined {
  for (const route of filterAuthorizedRoutes(items, access)) {
    const path = route.routes
      ? firstAuthorizedPath(route.routes, access)
      : route.path;
    if (path) return path;
  }
  return undefined;
}
export type CenterKey =
  | 'employee'
  | 'attendance'
  | 'payroll'
  | 'social-insurance'
  | 'performance';

export const CENTER_ROOTS: Record<CenterKey, string> = {
  employee: '/employee-center',
  attendance: '/attendance-center',
  payroll: '/payroll-center',
  'social-insurance': '/social-insurance-center',
  performance: '/performance-center',
};

export const NAVIGATION_ROOTS = { ...CENTER_ROOTS, settings: '/settings' };

export function getNavigationForPath(pathname: string): CenterKey | 'settings' {
  const path = pathname.split(/[?#]/, 1)[0];
  return path === '/settings' || path.startsWith('/settings/')
    ? 'settings'
    : getCenterForPath(path);
}

export function getSettingsPath(access: Access) {
  return firstAuthorizedPath(
    routes.filter((route) => route.path === '/settings'),
    access,
  );
}

export function getCenterForPath(pathname: string): CenterKey {
  const match = (Object.entries(CENTER_ROOTS) as [CenterKey, string][])
    .sort(([, left], [, right]) => right.length - left.length)
    .find(([, root]) => pathname === root || pathname.startsWith(`${root}/`));
  return match?.[0] || 'employee';
}

export function getDefaultPath(
  access: Access,
  settings = false,
  center: CenterKey = 'employee',
) {
  const scope = routes.filter((route) =>
    settings ? route.path === '/settings' : route.path === CENTER_ROOTS[center],
  );
  return (
    firstAuthorizedPath(scope, access) ||
    firstAuthorizedPath(
      routes.filter(
        (route) =>
          route.path && Object.values(CENTER_ROOTS).includes(route.path),
      ),
      access,
    ) ||
    firstAuthorizedPath(
      routes.filter((route) => route.path === '/settings'),
      access,
    )
  );
}
const toRouterRoutes = (items: AppRoute[]): any[] =>
  items.map(({ routes: children, ...route }) => ({
    ...route,
    children: children ? toRouterRoutes(children) : undefined,
  }));
export function getRequiredPermissionForPath(pathname: string) {
  const matches = matchRoutes(toRouterRoutes(routes), pathname);
  return matches
    ?.map((match) => match.route.permissionResourceCode)
    .filter(Boolean)
    .at(-1) as string | undefined;
}

export function getSplitMenuSelectedKeys(pathname: string): string[] {
  const centerRoot = NAVIGATION_ROOTS[getNavigationForPath(pathname)];
  const cleanPathname = pathname.split(/[?#]/, 1)[0] || centerRoot;
  const matchedRoute = matchRoutes(toRouterRoutes(routes), cleanPathname)?.at(
    -1,
  )?.route as AppRoute | undefined;
  const parentKey = matchedRoute?.parentKeys?.at(-1);
  const leafKey =
    parentKey ||
    (matchedRoute?.name && !matchedRoute.hideInMenu
      ? matchedRoute.path
      : undefined);

  return [centerRoot, leafKey || centerRoot];
}
